"use client";

import { useEffect, useState } from "react";
import { fetchApi } from "@/lib/api-fetch";
import { apiFetchPath } from "@/lib/api-fetch-path";

/**
 * Whether the marketplace is switched on, for the nav entry.
 *
 * The API answers 404 on every marketplace route while the feature is off, so
 * a one-row browse probe is the signal. It starts false (the entry never
 * flashes in before the probe resolves) and fails silent, like the Live tab's
 * schedule probe. One probe per page load is shared by every nav instance.
 */
let probe: Promise<boolean> | null = null;

function probeMarketplace(): Promise<boolean> {
  if (!probe) {
    probe = fetchApi(apiFetchPath("/api/v1/marketplace/listings?limit=1"))
      .then((res) => res.ok)
      .catch(() => {
        // A network blip should not hide the entry for the rest of the session.
        probe = null;
        return false;
      });
  }
  return probe;
}

export function useMarketplaceAvailable(): boolean {
  const [available, setAvailable] = useState(false);
  useEffect(() => {
    let cancelled = false;
    void probeMarketplace().then((ok) => {
      if (!cancelled) setAvailable(ok);
    });
    return () => {
      cancelled = true;
    };
  }, []);
  return available;
}

/** Test hook: forget the cached probe. */
export function resetMarketplaceAvailabilityProbe(): void {
  probe = null;
}
